import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import 'i18next';
import React, { createContext, useCallback, useContext } from 'react';
import type {
	PeticeClenPeticnihoVyboruSeznamDto,
	PeticePeticeDto,
	ResponseError,
} from '@gov-nx/api';
import {
	ePetitionDetailQuery,
	ePetitionMembersQuery,
	filterErrorStatus,
	useAxiosInstance,
	usePoQueries,
} from '@gov-nx/api';
import { useIdentity } from '@gov-nx/auth/common';
import { GovError } from '@gov-nx/core/app';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import {
	AUTHORIZATION_SERVICE_NAME_EPETICE_PODEPSAT,
	AUTHORIZATION_SERVICE_NAME_EPETICE_ODVOLAT,
	usePetitionCloseSubmit,
	AUTHORIZATION_SERVICE_NAME_EPETICE_PODAT,
	usePetitionExtend,
	usePetitionPetitionersAttachments,
	usePetitionSign,
	usePoIndividualDataLoad,
	useProcessControl,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import {
	useFormBuilderWithConsent,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { useDataBoxStoreLists } from '@gov-nx/store';
import { toDateReadable, useBoolean } from '@gov-nx/utils/common';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import type { PageCode } from '../../definitions/codes';
import { usePetitionAttachmentsQuery } from '../hooks/usePetitionAttachmentsQuery';
import type {
	EPetitionDetailContext,
	EPetitionDetailProcessControl,
} from './context.types';
import { isPetitionEditable } from './utils';

const EPeticeDetailContext =
	createContext<Nullable<EPetitionDetailContext>>(null);

interface EPeticeDetailContextProviderProps {
	petitionId: number;
	children: React.ReactNode;
	code: PageCode;
}

export function EPeticeDetailContextProvider({
	children,
	code,
	petitionId,
}: EPeticeDetailContextProviderProps) {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const { toastMessageSuccess } = useMessageEvents();
	const { setControls, controls } =
		useProcessControl<EPetitionDetailProcessControl>({
			initialLoading: true,
			isRevokeModal: false,
			isSignModal: false,
			isSignProcessing: false,
			isRevokeProcessing: false,
			isCloseAndSubmitModal: false,
			isCloseModal: false,
			isExtendModal: false,
			isExtendProcessing: false,
			isCloseProcessing: false,
			isSubmitAndCloseProcessing: false,
			isSubmitProcessing: false,
		});
	const isDropdownOpen = useBoolean(false);
	const { axiosInstance } = useAxiosInstance();
	const { downloadEPetition } = useDownloadEvents();
	const loginInfo = useIdentity();

	const { connectedDataBoxes } = useDataBoxStoreLists();
	const isDataBoxConnected = connectedDataBoxes.length > 0;

	const authContext = useAutorizaceContext();

	const { individualPerson } = usePoIndividualDataLoad({
		onError: (initialError) => setControls({ initialError }),
	});

	const petitionAttachmentsQuery = usePetitionAttachmentsQuery(petitionId);

	const [petitionQuery, petitionMembersQuery] = usePoQueries<
		[
			UseQueryResult<PeticePeticeDto, AxiosError<ResponseError>>,
			UseQueryResult<
				PeticeClenPeticnihoVyboruSeznamDto,
				AxiosError<ResponseError>
			>,
		]
	>({
		queries: [
			{
				queryKey: ['epetice-detail', petitionId],
				queryFn: () => ePetitionDetailQuery(axiosInstance, petitionId),
				refetchOnWindowFocus: false,
				retry: 0,
				onError: (error: Error) =>
					setControls({ initialError: new GovError(error.message) }),
			},
			{
				queryKey: ['epetice-members', petitionId],
				queryFn: () => ePetitionMembersQuery(axiosInstance, petitionId),
				refetchOnWindowFocus: false,
				retry: 0,
			},
		],
		configs: [
			{
				errorIgnoreFilter: filterErrorStatus(400, (initialError) =>
					setControls({ initialError })
				),
			},
			{
				errorIgnoreFilter: filterErrorStatus(400, (initialError) =>
					setControls({ initialError })
				),
			},
		],
		onAllSuccess: () => {
			setControls({ initialLoading: false });
		},
	});

	const petition = petitionQuery.data;
	const members = petitionMembersQuery.data?.seznam ?? [];
	const attachments = petitionAttachmentsQuery.attachments;

	const refresh = useCallback(async () => {
		await petitionQuery.refetch();
	}, [petitionQuery]);

	const ePetitionPetitionersAttachments = usePetitionPetitionersAttachments({
		petitionId,
		petition,
		code,
	});

	const { isLowAuthorization } = useServiceAuthorization(
		AUTHORIZATION_SERVICE_NAME_EPETICE_PODEPSAT
	);
	const { isLowAuthorization: isLowAuthorizationOdvolat } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME_EPETICE_ODVOLAT);
	const { isLowAuthorization: isLowAuthorizationPodat } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME_EPETICE_PODAT);

	const signFormDefinition = useFormBuilderWithConsent<
		Record<never, never>,
		unknown
	>([], {
		isDisabled: controls.isSignProcessing,
		isLowAuthorization,
	});

	const revokeFormDefinition = useFormBuilderWithConsent<
		Record<never, never>,
		unknown
	>([], {
		isDisabled: controls.isRevokeProcessing,
		isLowAuthorization: isLowAuthorizationOdvolat,
	});

	const submitFormDefinition = useFormBuilderWithConsent<
		Record<never, never>,
		unknown
	>([], {
		isDisabled: controls.isSubmitProcessing,
		isLowAuthorization: isLowAuthorizationPodat,
	});

	const submitCloseFormDefinition = useFormBuilderWithConsent<
		Record<never, never>,
		unknown
	>([], {
		isDisabled: controls.isSubmitAndCloseProcessing,
		isLowAuthorization: isLowAuthorizationPodat,
	});

	const ePetitionSign = usePetitionSign({
		petition: petition ? petition : null,
		onSignSuccess: async (polozkaAutorizaceId?: string) => {
			await refresh();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(ts('zprava.petice-podepsana'));
				setControls({ isSignModal: false, isSignProcessing: false });
				signFormDefinition.formMethods.reset();
			};

			if (authContext && polozkaAutorizaceId) {
				setControls({ isSignModal: false });
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME_EPETICE_PODEPSAT,
					polozkaAutorizaceId,
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onSignError: () => {
			setControls({ isSignModal: false, isSignProcessing: false });
			signFormDefinition.formMethods.reset();
		},
		onRevokeSuccess: async (polozkaAutorizaceId?: string) => {
			await refresh();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(ts('zprava.petice-odvolana'));
				setControls({ isRevokeModal: false, isRevokeProcessing: false });
				revokeFormDefinition.formMethods.reset();
			};

			if (authContext && polozkaAutorizaceId) {
				setControls({ isRevokeModal: false });
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME_EPETICE_ODVOLAT,
					polozkaAutorizaceId,
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onRevokeError: () => {
			setControls({ isRevokeModal: false, isRevokeProcessing: false });
			revokeFormDefinition.formMethods.reset();
		},
	});

	const handleSignSubmit = signFormDefinition.formMethods.handleSubmit(() => {
		setControls({ isSignProcessing: true });
		ePetitionSign.signQuery.mutate({
			petitionId: petition?.id as number,
			axiosInstance,
		});
	});

	const handleRevokeSubmit = revokeFormDefinition.formMethods.handleSubmit(
		() => {
			setControls({ isRevokeProcessing: true });
			ePetitionSign.revokeQuery.mutate({
				petitionId: petition?.id as number,
				axiosInstance,
			});
		}
	);

	const ePetitionExtend = usePetitionExtend({
		petition: petition ? petition : null,
		onExtendSuccess: async () => {
			const { data } = await petitionQuery.refetch();

			data?.datumUkonceni &&
				toastMessageSuccess(
					ts('zprava.petice-prodlouzena', {
						prodlouzeniDo: toDateReadable(data.datumUkonceni),
					})
				);
			setControls({ isExtendProcessing: false, isExtendModal: false });
		},
		onExtendError: () =>
			setControls({ isExtendProcessing: false, isExtendModal: false }),
	});

	const ePetitionSubmitClose = usePetitionCloseSubmit({
		petition: petition ? petition : null,
		onCloseSuccess: async () => {
			await refresh();
			setControls({ isCloseProcessing: false, isCloseModal: false });
			isDropdownOpen.setFalse();
			toastMessageSuccess(ts('zprava.petice-uzavrena'));
		},
		onCloseError: () => {
			setControls({ isCloseProcessing: false, isCloseModal: false });
			isDropdownOpen.setFalse();
		},
		onSubmitAndClosedSuccess: async (polozkaAutorizaceId?: string) => {
			await refresh();

			const nonHighAuthorizationFlow = () => {
				setControls({
					isSubmitAndCloseProcessing: false,
					isCloseAndSubmitModal: false,
				});
				toastMessageSuccess(ts('zprava.petice-uzavrena-podana'));
				submitCloseFormDefinition.formMethods.reset();
			};

			if (authContext && polozkaAutorizaceId) {
				setControls({ isCloseAndSubmitModal: false });
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME_EPETICE_PODAT,
					polozkaAutorizaceId,
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onSubmitAndClosedError: () => {
			setControls({
				isSubmitAndCloseProcessing: false,
				isCloseAndSubmitModal: false,
			});
			submitCloseFormDefinition.formMethods.reset();
		},
		onSubmitSuccess: async (polozkaAutorizaceId?: string) => {
			await refresh();

			const nonHighAuthorizationFlow = () => {
				setControls({ isSubmitProcessing: false });
				toastMessageSuccess(ts('zprava.petice-podana'));
				submitFormDefinition.formMethods.reset();
			};

			if (authContext && polozkaAutorizaceId) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME_EPETICE_PODAT,
					polozkaAutorizaceId,
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onSubmitError: () => {
			setControls({ isSubmitProcessing: false });
			submitFormDefinition.formMethods.reset();
		},
	});

	const handleEPetitionDownload = () => {
		setControls({ isDownloadProcessing: true });
		return downloadEPetition({
			petitionId,
			callback: () => {
				setControls({ isDownloadProcessing: false });
			},
		});
	};

	const handleSubmitPetitionSubmit =
		submitFormDefinition.formMethods.handleSubmit(() => {
			setControls({ isSubmitProcessing: true });
			ePetitionSubmitClose.submitQuery.mutate({ petitionId, axiosInstance });
		});

	const handleSubmitCloseSubmit =
		submitCloseFormDefinition.formMethods.handleSubmit(() => {
			setControls({ isSubmitAndCloseProcessing: true });
			ePetitionSubmitClose.submitAndCloseQuery.mutate({
				petitionId,
				axiosInstance,
			});
		});

	const handleEPetitionClose = () => {
		setControls({ isCloseProcessing: true });
		ePetitionSubmitClose.closeQuery.mutate({ petitionId, axiosInstance });
	};

	const onCloseModal = () => {
		setControls({ isCloseModal: false });
		isDropdownOpen.setFalse();
	};

	return (
		<EPeticeDetailContext.Provider
			value={{
				petition,
				isEditable: petition ? isPetitionEditable(petition) : false,
				members,
				attachments,
				individualPerson,
				isDataBoxConnected,
				controls: {
					...controls,
					initialError: controls.initialError ?? petitionAttachmentsQuery.error,
				},
				isDropdownOpen,
				ePetitionPetitionersAttachments,
				ePetitionSign,
				ePetitionExtend,
				ePetitionSubmitClose,
				signFormDefinition,
				handleSignSubmit,
				revokeFormDefinition,
				handleRevokeSubmit,
				submitFormDefinition,
				handleSubmitPetitionSubmit,
				submitCloseFormDefinition,
				handleSubmitCloseSubmit,
				loginInfo,
				handleEPetitionDownload,
				handleEPetitionClose,
				onCloseModal,
				setControls,
			}}>
			{children}
		</EPeticeDetailContext.Provider>
	);
}

export const useEPeticeDetailContext = (): EPetitionDetailContext =>
	useContext(EPeticeDetailContext) as EPetitionDetailContext;
